import React, { useEffect, useMemo, useState } from 'react';
import {
    Grid2,
    Table,
    TableContainer,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import SkeletonTable from '../../../components/atom/SkeletonTable';
import SortableTableCell from '../../../components/atom/SortableTableCell';
import { TABLE_ROW_COUNT_OPTIONS } from '../../../helpers/constants';
import { handleChangeRowsPerPage, handlePageChange } from '../../../helpers/handleTableFunctions';
import { useCapabilities } from '../../../hooks/useCapabilities';
import CheckinBookingRow from './CheckinBookingRow';
import CheckinGroupHeader from './CheckinGroupHeader';
import CheckinGroupFooter from './CheckinGroupFooter';
import { COL_COUNT, computeGroupStats, getPrimaryTicketTypeName } from './checkinViewHelpers';

const CheckinViewTable = ({
    rows,
    timeslotId,
    limit,
    page,
    setParams,
    total,
    loading,
    sort,
    sortDir,
    setSorting,
    reloadData,
    reloadTimeslot,
    saveCheckinNote,
    disabled = false,
    groupByAgeBand = false,
    rowsPerPageOptions = TABLE_ROW_COUNT_OPTIONS,
    slotTemplate = [],
}) => {
    const { t } = useTranslation();
    const { can, CAPABILITIES } = useCapabilities();
    const canManageBookings = can(CAPABILITIES.BOOKINGS_MANAGE);
    const [editingNotes, setEditingNotes] = useState({});
    const [expandedRows, setExpandedRows] = useState({});
    const [collapsedGroups, setCollapsedGroups] = useState(new Set());

    const handleNoteChange = (id, value) => {
        setEditingNotes((prev) => ({ ...prev, [id]: value }));
    };

    const handleNoteBlur = async (id) => {
        if (editingNotes[id] === undefined) return;
        const row = rows?.find((r) => r.id === id);
        const original = row?.checkinNote ?? '';
        if (editingNotes[id] !== original && saveCheckinNote) {
            // Pass the row's own timeslot id - overlapping sibling rows live on
            // another slot and the backend's check-in proof expects theirs.
            await saveCheckinNote(id, editingNotes[id], row?.timeslotId);
        }
        setEditingNotes((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
        });
    };

    const handleExpandClick = (bookingId) => {
        setExpandedRows((prev) => ({ ...prev, [bookingId]: !prev[bookingId] }));
    };

    const toggleGroup = (name) => {
        setCollapsedGroups((prev) => {
            const next = new Set(prev);
            if (next.has(name)) next.delete(name);
            else next.add(name);
            return next;
        });
    };

    useEffect(() => {
        if (rows?.length === 0 && page > 0) {
            setParams({ limit, page: page - 1 });
        }
    }, [rows, limit, page, setParams]);

    useEffect(() => {
        setExpandedRows({});
        setCollapsedGroups(new Set());
    }, [page, limit, sort, sortDir]);

    const groupedRows = useMemo(() => {
        if (!Array.isArray(rows)) return [];
        const groups = {};
        for (const row of rows) {
            const typeName = getPrimaryTicketTypeName(row.ticketTypes);
            if (!groups[typeName]) groups[typeName] = [];
            groups[typeName].push(row);
        }
        return Object.entries(groups).map(([name, bookings]) => ({ name, bookings }));
    }, [rows]);

    // Drop collapsed entries whose group no longer exists (e.g. after reloadData
    // following a checkin), so stale names don't accumulate in the Set.
    useEffect(() => {
        setCollapsedGroups((prev) => {
            if (prev.size === 0) return prev;
            const valid = new Set(groupedRows.map((g) => g.name));
            const next = new Set();
            for (const name of prev) {
                if (valid.has(name)) next.add(name);
            }
            return next.size === prev.size ? prev : next;
        });
    }, [groupedRows]);

    return (
        <Grid2 size={{ xs: 12 }}>
            {/*
             * Fixed column widths: with the default `auto` layout the columns
             * are sized from cell content, so toggling "group by age band"
             * makes every column jump. Pinning widths keeps the layout stable.
             */}
            <TableContainer>
                <Table stickyHeader sx={{ tableLayout: 'fixed', minWidth: 900 }}>
                    <colgroup>
                        <col style={{ width: '8%' }} />
                        <col style={{ width: '13%' }} />
                        <col style={{ width: '10%' }} />
                        <col style={{ width: '11%' }} />
                        <col style={{ width: '28%' }} />
                        <col style={{ width: '18%' }} />
                        <col style={{ width: '4%' }} />
                        <col style={{ width: '8%' }} />
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            <SortableTableCell
                                arrow={sort === 'isCheckedIn' ? sortDir : ''}
                                onClick={() => setSorting('isCheckedIn')}
                            >
                                {t('bookings.columns.isCheckedIn')}
                            </SortableTableCell>
                            <SortableTableCell
                                arrow={sort === 'name' ? sortDir : ''}
                                onClick={() => setSorting('name')}
                            >
                                {t('common.name')}
                            </SortableTableCell>
                            <TableCell>{t('bookingitems.columns.ticketSeatName')}</TableCell>
                            <TableCell>{t('bookingitems.columns.ticketCount')}</TableCell>
                            <TableCell>{t('bookings.columns.note')}</TableCell>
                            <TableCell>{t('bookings.columns.checkinNote')}</TableCell>
                            <TableCell sx={{ width: 48 }} />
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <SkeletonTable rows={limit} cells={COL_COUNT} />
                        ) : (
                            groupedRows.map((group) => {
                                const isCollapsed = collapsedGroups.has(group.name);
                                const stats = computeGroupStats(group.bookings, { groupByAgeBand });
                                return (
                                    <React.Fragment key={group.name}>
                                        <CheckinGroupHeader
                                            name={group.name}
                                            count={group.bookings.length}
                                            collapsed={isCollapsed}
                                            onToggle={() => toggleGroup(group.name)}
                                        />
                                        {!isCollapsed &&
                                            group.bookings.map((row) => (
                                                <CheckinBookingRow
                                                    key={row.id}
                                                    row={row}
                                                    timeslotId={timeslotId}
                                                    disabled={disabled}
                                                    groupByAgeBand={groupByAgeBand}
                                                    expanded={!!expandedRows[row.id]}
                                                    onToggleExpand={handleExpandClick}
                                                    editingNote={editingNotes[row.id]}
                                                    onNoteChange={handleNoteChange}
                                                    onNoteBlur={handleNoteBlur}
                                                    reloadData={reloadData}
                                                    reloadTimeslot={reloadTimeslot}
                                                    canManageBookings={canManageBookings}
                                                    slotTemplate={slotTemplate}
                                                />
                                            ))}
                                        <CheckinGroupFooter
                                            checkedIn={stats.checkedIn}
                                            total={group.bookings.length}
                                            totalTickets={stats.totalTickets}
                                            summaryEntries={stats.summaryEntries}
                                        />
                                    </React.Fragment>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                count={total || 0}
                rowsPerPage={limit}
                component="div"
                page={rows?.length <= 0 ? 0 : page}
                onPageChange={(_, newPage) => handlePageChange(newPage, setParams)}
                rowsPerPageOptions={rowsPerPageOptions}
                onRowsPerPageChange={(event) => handleChangeRowsPerPage(event, setParams)}
                labelRowsPerPage={t('labelRowsPerPage')}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
            />
        </Grid2>
    );
};

export default CheckinViewTable;
